import React, { type FunctionComponent, useCallback } from 'react';
import { FilterItem, type FilterItemProps } from '@atlassian/search-dialog';
import { useAnalytics } from '../analytics/use-analytics';
import { onFilterSelect, onFilterUnselect, selectEvent } from '../analytics/events';
import { type FilterOptionSource } from '../filters/types';
import { type ProductFilterType } from '../clients/common-types';
import { type OptionData, type Option } from '@atlaskit/smart-user-picker';

export type OnChangeHandler = (value: string, type: ProductFilterType, isChecked: boolean) => any;

interface Props extends Omit<FilterItemProps, 'onChange'> {
	filterType: ProductFilterType;
	LabelComponent: React.ElementType;
	onChange?: OnChangeHandler;
	defaultChecked?: boolean;
	filterSource: FilterOptionSource;
	isDisabled?: boolean;
	smartValue?: Option<OptionData>;
	allOptions?: OptionData[];
	collabGraphSessionId?: string;
	variant?: string;
}

export const ProductFilterItem: FunctionComponent<Props> = ({
	onChange,
	filterType,
	filterSource,
	isDisabled = false,
	smartValue,
	allOptions,
	collabGraphSessionId = '',
	variant,
	...rest
}: Props) => {
	const { fireAnalyticsEvent } = useAnalytics();
	const onChangeCallback = useCallback(
		(id: string, checked: boolean) => {
			if (checked) {
				fireAnalyticsEvent(
					onFilterSelect({
						filterId: id,
						filterSource,
						filterType,
					}),
				);
				if (smartValue && allOptions) {
					fireAnalyticsEvent(
						selectEvent(smartValue, allOptions, 'quickSearch', collabGraphSessionId),
					);
				}
			} else {
				fireAnalyticsEvent(
					onFilterUnselect({
						filterId: id,
						filterSource,
						filterType,
					}),
				);
			}

			onChange?.(id, filterType, checked);
		},
		[
			onChange,
			filterType,
			fireAnalyticsEvent,
			filterSource,
			smartValue,
			allOptions,
			collabGraphSessionId,
		],
	);

	return (
		<FilterItem {...rest} onChange={onChangeCallback} isDisabled={isDisabled} variant={variant} />
	);
};
